<script>
  import {getImageUrl} from "../lib/images";
  import {onMount, onDestroy, tick} from 'svelte';
  import UserDataProvider from "../users/UserDataProvider.svelte";
  import Markdown from "../general/Markdown.svelte";
  import moment from 'moment';
  import Og from "./Og.svelte";
  import _ from 'lodash';
  import {uploadingImages, editingMessageId} from './messageStore';
  import {navigate} from "svelte-routing";
  import ContextMenu from "../context-menu/ContextMenu.svelte";
  import {showConfirm} from "../popup/popupStore";
  import {deleteMessage, editMessage} from "./messageHelper";
  import {showSnackbar} from "../snackbar/snackbarStore";
  import {MenuItem} from "../constants";
  import {showModal} from "../modal/modalStore";
  import MessageThread from '../messages/MessageThread.svelte';

  export let message;
  export let showProfile = false;
  export let onMessageClick = null;
  export let hideCommentCount = false;

  let messageInputElement = null;

  const getYoutubeVideoId = url => {
    if (!url) return null;

    const regExp = /(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&? ]*).*/;
    const match = url.match(regExp);

    if (match && match[2].length === 11) {
      return match[2];
    } else {
      return null;
    }
  };

  const getVideoHtml = (videoId, width, height) => {
    return `<iframe width="${width}" height="${height}" src="//www.youtube.com/embed/${videoId}" frameborder="0" allowfullscreen/>`;
  };

  let messageAreaWidth = 0;

  $: youtubeVideoId = getYoutubeVideoId(message.data().body);

  const showDeleteMessageConfirm = async () => {
    if (!await showConfirm('Are you sure you want to delete this message?')) return;

    deleteMessage(message.id);
  };

  const showThread = () => {
    showModal(MessageThread, {
      modalProps: {
        title: 'Thread'
      },
      contentProps: {message}
    });
  };

  let contextMenuVisible = false;

  const contextMenuItems = _.compact([
    !hideCommentCount && {
      icon: 'chatbubbles-outline',
      text: 'Comments',
      action: showThread
    },
    {
      icon: 'copy-outline', text: 'Copy text', action: () => {
        const input = document.createElement('textarea');
        input.style = 'opacity: 0; pointer-events: none; position: fixed; top: 100%; left: 100%;';
        document.body.appendChild(input);
        input.value = message.data().body;
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);

        showSnackbar('Copied!');
      }
    },
    {
      icon: 'pencil-outline', text: 'Edit message', action: () => {
        editingMessageId.set(message.id);
      }
    },
    MenuItem.divider,
    {icon: 'trash-outline', text: 'Delete message', action: showDeleteMessageConfirm}
  ]);


  const submitEditForm = () => {
    const body = messageInputElement.innerText.trim();
    if (!body) {
      deleteMessage(message.id)
    } else {
      editMessage(message.id, {body});
    }
    editingMessageId.set(null);
  };

  const onMessageEditInputKeyDown = e => {
    if (e.key === 'Escape') {
      editingMessageId.set(null);
      return;
    }

    if (e.key === 'Enter') {
      if (!e.shiftKey) {
        e.preventDefault();
        submitEditForm();
      }
    }
  };

  let unsubscribeEditingMessageId;
  onMount(() => {
    unsubscribeEditingMessageId = editingMessageId.subscribe(async val => {
      if (val === message.id) {
        await tick();
        messageInputElement.innerText = message.data().body;
        messageInputElement.focus();
      }
    });
  });


  onDestroy(() => unsubscribeEditingMessageId && unsubscribeEditingMessageId());
</script>

<row class="message-list-item" class:menu-open={contextMenuVisible}>
  <UserDataProvider userId={message.data().userId} let:item>
      {#if !!item && showProfile}
        <img src="{getImageUrl(item.data().photoUrl, 80)}" alt="" class="profile-image">
      {:else}
        <div style="width: 56px;"/>
      {/if}

    <ContextMenu
        bind:visible={contextMenuVisible}
        style="flex: 1;"
        items={contextMenuItems}>
      <div
          class="message-body expand"
          class:clickable={!!onMessageClick}
          bind:clientWidth={messageAreaWidth}
          on:click={() => onMessageClick && onMessageClick(message)}>
          {#if !!item && showProfile}
            <row align-center class="profile-area">
              <div class="username">{item.data().displayName}</div>
              <div class="time">{moment(message.data().created.seconds * 1000).format('LT')}</div>
            </row>
          {/if}

          {#if $editingMessageId === message.id}
            <column class="edit-message-form">
              <div contenteditable bind:this={messageInputElement} class="edit-message-input" on:keydown={onMessageEditInputKeyDown}></div>
              <row>
                <button on:click={() => editingMessageId.set(null)}>Cancel</button>
                <button class="button-submit" on:click={submitEditForm}>Save</button>
              </row>
            </column>
          {:else}
            <Markdown content={message.data().body}/>
          {#if !hideCommentCount && !!message.data().commentCount}
            <button
                    class="button-open-thread clickable row align-center"
                    on:click={showThread}
            >{message.data().commentCount} {message.data().commentCount === 1 ? 'comment' : 'comments'} <ion-icon name="caret-forward-outline"></ion-icon></button>
          {/if}
              {#if youtubeVideoId}
                  {@html getVideoHtml(youtubeVideoId, messageAreaWidth, messageAreaWidth / 16 * 9)}
              {:else if message.data().og}
                  {#each message.data().og as og}
                    <Og
                        url={og.url}
                        title={og.title}
                        description={og.description}
                        imageUrl={_.get(og, 'image.url')}
                        siteName={og.site_name}
                    />
                  {/each}
              {/if}

              {#each (message.data().images || []) as image}
                <expand class="message-wrap">
                  <img
                      style="width: 100%; height: {image.imageSize ? (messageAreaWidth / image.imageSize.width * image.imageSize.height) + 'px' : 'auto'}; background-color: #efefef;"
                      src="{getImageUrl(_.get(image, ['downloadURLs', 0]), Math.ceil(messageAreaWidth / 200) * 200)}"
                      alt="">
                </expand>
              {/each}
              {#each ($uploadingImages[message.id] || []) as uploadingImage}
              <!--   don't need to set height. local image will be loaded so fast -->
                <expand class="message-wrap">
                  <img
                      style="width: 100%; background-color: #efefef;"
                      src="{URL.createObjectURL(uploadingImage.file)}"
                      alt="">
                </expand>
              {/each}
          {/if}
      </div>
    </ContextMenu>
  </UserDataProvider>
</row>

<style>
  .message-list-item {
    width: 100%;
    padding: 4px 0;
  }

  .message-list-item.menu-open, .message-list-item:hover {
    background-color: #fafafa;
  }

  .profile-image {
    width: 40px;
    height: 40px;
    margin: 4px 8px;
    border-radius: 4px;
  }

  .message-body {
    flex-shrink: 1;
    margin-right: 8px;
  }

  .profile-area {
    margin-bottom: 4px;
  }

  .username {
    line-height: 1.5em;
    font-weight: bold;
    margin-right: 4px;
  }

  .time {
    color: #ccc;
    font-size: 0.8em;
  }

  .edit-message-input {
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 4px 8px;
    margin-bottom: 4px;
    white-space: pre-wrap;
    line-height: 1.5em;
  }

  .edit-message-form button {
    border: 1px solid #ddd;
    background-color: white;
    border-radius: 4px;
    height: 28px;
    margin-right: 4px
  }

  .edit-message-form button.button-submit {
    color: white;
    background-color: black;
  }

  .button-open-thread {
    background: none;
    border: none;
    height: 24px;
    padding: 0;
    color: #888;
    font-size: .9rem;
  }


</style>